// src/components/pages/ScratchGameDetails.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../layout/AppShell";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Badge from "../ui/Badge";
import Icon from "../ui/Icon";
import Spinner from "../ui/Spinner";
import ScratchScore from "../scratch/ScratchScore";
import RemainingPrizeChart from "../scratch/RemainingPrizeChart";
import ScoreHistoryChart from "../scratch/ScoreHistoryChart";
import PrizeTierTable from "../scratch/PrizeTierTable";
import PremiumLock from "../scratch/PremiumLock";
import ResponsibleGamblingDisclaimer from "../scratch/ResponsibleGamblingDisclaimer";
import {
  getGameDetail,
  getScoreHistory,
  formatMoney,
  formatRatioPct,
  formatDate,
} from "../../services/scratchService";

/**
 * `isPremium` is read here from whatever the app already exposes (Redux user
 * slice / context). Wire it to the real entitlement; it defaults to true so
 * the page is fully visible until the integrator gates it.
 */
function StatTile({ icon, label, value, tone = "var(--txt)" }) {
  return (
    <Card variant="soft">
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center justify-center rounded-full shrink-0"
          style={{ width: 34, height: 34, background: "var(--surf-3)", color: "var(--txt-2)" }}
        >
          <Icon name={icon} size={17} />
        </span>
        <div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>
            {label}
          </div>
          <div className="font-semibold text-[15px] tabular-nums" style={{ color: tone }}>
            {value}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function ScratchGameDetails({ isPremium = true }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const [detail, hist] = await Promise.all([getGameDetail(id), getScoreHistory(id)]);
      if (!alive) return;
      if (!detail) {
        setNotFound(true);
      } else {
        setGame(detail);
        setHistory(hist);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-24" style={{ color: "var(--txt-2)" }}>
          <Spinner size={20} />
          <span className="ml-2 text-[13px]">Loading game…</span>
        </div>
      </AppShell>
    );
  }

  if (notFound || !game) {
    return (
      <AppShell>
        <div className="px-4 py-16 text-center space-y-4">
          <Icon name="ticket-off" size={28} className="text-[var(--txt-3)]" />
          <p style={{ color: "var(--txt-2)" }}>That game couldn't be found.</p>
          <Button variant="ghost" icon="arrow-left" onClick={() => navigate("/scratch")}>
            Back to dashboard
          </Button>
        </div>
      </AppShell>
    );
  }

  const ended = game.status === "ENDED";

  return (
    <AppShell>
      <div className="px-4 py-8 space-y-6">
        <button
          type="button"
          onClick={() => navigate("/scratch")}
          className="inline-flex items-center gap-1.5 text-[13px]"
          style={{ color: "var(--txt-2)" }}
        >
          <Icon name="arrow-left" size={15} />
          Dashboard
        </button>

        <Card variant="panel">
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="neutral">{game.stateCode}</Badge>
                <Badge variant="neutral">#{game.gameNumber}</Badge>
                {ended ? (
                  <Badge variant="red">Ended</Badge>
                ) : (
                  <Badge variant="teal">Active</Badge>
                )}
              </div>
              <h1
                className="font-semibold mt-2"
                style={{ fontSize: 24, color: "var(--on-panel)" }}
              >
                {game.gameName || `Game #${game.gameNumber}`}
              </h1>
              <p className="text-[13px] mt-1" style={{ color: "var(--on-panel-2)" }}>
                {formatMoney(game.ticketPrice)} ticket · updated {formatDate(game.lastUpdated)}
              </p>
            </div>
            <ScratchScore score={game.scratchScore} size="lg" />
          </div>
        </Card>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            icon="trophy"
            label="Top prizes remaining"
            value={`${formatRatioPct(game.topPrizeRemainingRatio)}`}
            tone="var(--gold)"
          />
          <StatTile
            icon="coin"
            label="All prizes remaining"
            value={formatRatioPct(game.totalPrizeRemainingRatio)}
            tone="var(--teal)"
          />
          <StatTile
            icon="sparkles"
            label="Relative value (state)"
            value={Number(game.estimatedEv).toFixed(3)}
          />
          <StatTile
            icon="bolt"
            label="Mid-tier strength"
            value={`${Math.round(Number(game.midTierStrength) || 0)}`}
          />
        </div>

        <Card>
          <h3 className="font-semibold text-[15px] mb-3" style={{ color: "var(--txt)" }}>
            Remaining prizes by tier
          </h3>
          <RemainingPrizeChart tiers={game.prizeTiers} />
          <ResponsibleGamblingDisclaimer variant="compact" className="mt-4" />
        </Card>

        <PremiumLock locked={!isPremium}>
          <div className="space-y-6">
            <Card>
              <h3 className="font-semibold text-[15px] mb-3" style={{ color: "var(--txt)" }}>
                Scratch Score history
              </h3>
              <ScoreHistoryChart points={history} />
            </Card>

            <Card>
              <h3 className="font-semibold text-[15px] mb-3" style={{ color: "var(--txt)" }}>
                Full prize breakdown
              </h3>
              <PrizeTierTable tiers={game.prizeTiers} />
            </Card>
          </div>
        </PremiumLock>

        <ResponsibleGamblingDisclaimer />
      </div>
    </AppShell>
  );
}
